// 치료 이력 타임라인 — SVG 렌더러.
//   좌표는 전부 timelineLayout.buildLayout 이 계산한다. 여기서는 그리기만 한다.
//   이미지 저장(PNG/SVG)이 이 SVG 를 그대로 직렬화하므로 CSS 가 아니라 속성으로 스타일을 준다.

import React from 'react';
import { FONT } from './timelineLayout';

function Triangle({ x, y, size = 6, down = true, fill, stroke }) {
    const pts = down
        ? `${x - size},${y} ${x + size},${y} ${x},${y + size * 1.7}`
        : `${x - size},${y + size * 1.7} ${x + size},${y + size * 1.7} ${x},${y}`;
    return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={1} strokeLinejoin="round" />;
}

function LegendChip({ row, x, y, P }) {
    if (row.chip === 'single') {
        return <Triangle x={x + 13} y={y - 6} fill={row.fills[0]} stroke={row.stroke} />;
    }
    if (row.chip === 'event') {
        return <Triangle x={x + 13} y={y - 7} down={false} fill={row.fills[0]} stroke={row.fills[0]} />;
    }
    if (row.chip === 'none') {
        return <rect x={x} y={y - 7} width={26} height={14} rx={2} fill="#fff" stroke="#C9C7BE" strokeDasharray="2,2" />;
    }
    const h = 14 / row.fills.length;
    return (
        <g>
            {row.fills.map((f, i) => (
                <rect key={i} x={x} y={y - 7 + i * h} width={26} height={h} fill={f} />
            ))}
            {row.chip === 'fade' && <rect x={x} y={y - 7} width={26} height={14} fill={`url(#${P}-fade)`} />}
            <rect x={x} y={y - 7} width={26} height={14} rx={2} fill="none" stroke="#B4B2A9" strokeWidth={0.6} />
        </g>
    );
}

const TreatmentTimelineChart = React.forwardRef(function TreatmentTimelineChart({ layout, idPrefix = 'tt', t }, ref) {
    const L = layout;
    const P = idPrefix;
    const right = L.width - 28;

    return (
        <svg
            ref={ref}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${L.width} ${L.height}`}
            width="100%"
            fontFamily={FONT}
            style={{ display: 'block' }}
        >
            <defs>
                <pattern id={`${P}-hatch`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill="#fff" />
                    <line x1="0" y1="0" x2="0" y2="6" stroke="#D3D1C7" strokeWidth="1.6" />
                </pattern>
                {/* 종료일 미기재 — 오른쪽으로 갈수록 흐려진다 */}
                <linearGradient id={`${P}-fade`} x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0" stopColor="#fff" stopOpacity="0" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0.92" />
                </linearGradient>
                {L.bar && (
                    <clipPath id={`${P}-clip`}>
                        <rect x={L.bar.x} y={L.bar.y} width={L.bar.w} height={L.bar.h} rx="6" />
                    </clipPath>
                )}
            </defs>

            <rect width={L.width} height={L.height} fill="#fff" />
            <text x={28} y={30} fontSize="18" fontWeight="700" fill="#1F2328">{L.title}</text>

            {L.message && (
                <text x={28} y={58} fontSize="13" fill="#6B6B6B">{L.message}</text>
            )}

            {L.bar && (
                <g>
                    {L.markers.map((m, i) => (
                        <Triangle key={i} x={m.x} y={m.y} fill={m.fill} stroke={m.stroke} />
                    ))}

                    <g clipPath={`url(#${P}-clip)`}>
                        <rect x={L.bar.x} y={L.bar.y} width={L.bar.w} height={L.bar.h} fill="#fff" />
                        {L.segments.map((s, i) => {
                            if (!s.rows.length) {
                                return s.gap
                                    ? <rect key={i} x={s.x} y={L.bar.y} width={s.w} height={L.bar.h} fill={`url(#${P}-hatch)`} />
                                    : null;
                            }
                            const rh = L.bar.h / s.rows.length;
                            return (
                                <g key={i}>
                                    {s.rows.map((r, j) => (
                                        <rect key={j} x={s.x} y={L.bar.y + j * rh} width={s.w} height={rh} fill={r.fill} />
                                    ))}
                                    {s.rows.length > 1 && s.rows.slice(1).map((r, j) => (
                                        <line key={`h${j}`} x1={s.x} x2={s.x + s.w} y1={L.bar.y + (j + 1) * rh} y2={L.bar.y + (j + 1) * rh}
                                            stroke="#fff" strokeWidth="0.8" />
                                    ))}
                                    {s.fade && (
                                        <rect x={s.x + s.w - s.fadeW} y={L.bar.y} width={s.fadeW} height={L.bar.h} fill={`url(#${P}-fade)`} />
                                    )}
                                    {s.rows.map((r, j) => r.label && (
                                        <text key={`t${j}`} x={s.x + s.w / 2} y={L.bar.y + j * rh + rh / 2}
                                            textAnchor="middle" dominantBaseline="central"
                                            fontSize={r.size} fontWeight="500" fill={r.text}>
                                            {r.label}
                                        </text>
                                    ))}
                                </g>
                            );
                        })}
                        {L.segments.slice(1).map((s, i) => (
                            <line key={`v${i}`} x1={s.x} x2={s.x} y1={L.bar.y} y2={L.bar.y + L.bar.h} stroke="#fff" strokeWidth="1" />
                        ))}
                    </g>
                    <rect x={L.bar.x} y={L.bar.y} width={L.bar.w} height={L.bar.h} rx="6" fill="none" stroke="#B4B2A9" strokeWidth="0.8" />

                    {/* 눈금자 */}
                    {L.ticks.map((tk, i) => (
                        <g key={i}>
                            <line x1={tk.x} x2={tk.x} y1={L.rulerY} y2={L.rulerY + (tk.major ? 8 : 4)} stroke="#8A8A8A" strokeWidth="1" />
                            {tk.label && (
                                <text x={tk.x} y={L.rulerY + 21} fontSize="11.5" fill={tk.last ? '#1F2328' : '#444'}
                                    fontWeight={tk.last ? 600 : 400} textAnchor={tk.last ? 'end' : 'middle'}>
                                    {tk.label}
                                </text>
                            )}
                        </g>
                    ))}
                    {L.eventTicks.map((e, i) => (
                        <line key={`e${i}`} x1={e.x} x2={e.x} y1={L.bar.y + L.bar.h + 1} y2={L.rulerY + 10}
                            stroke={e.color} strokeWidth="2" opacity={e.faint ? 0.5 : 1} />
                    ))}

                    {/* 날짜 보조 줄 */}
                    <text x={28} y={L.dateY} fontSize="10.5" fill="#8A8A8A">{L.startLabel}</text>
                    {L.years.map((yr, i) => (
                        <g key={`y${i}`}>
                            <line x1={yr.x} x2={yr.x} y1={L.rulerY + 25} y2={L.dateY + 2} stroke="#BDBDBD" strokeDasharray="2,2" />
                            {yr.label && <text x={yr.x + 3} y={L.dateY} fontSize="10.5" fill="#8A8A8A">{yr.label}</text>}
                        </g>
                    ))}

                    <line x1={28} x2={right} y1={L.sepY} y2={L.sepY} stroke="#E6E6E6" />

                    {/* 이벤트 레인 */}
                    {L.eventLabels.map((e, i) => (
                        <text key={`l${i}`} x={e.x} y={e.y} fontSize="12" fontWeight="500" textAnchor={e.anchor}>
                            {e.right ? (
                                <>
                                    <tspan fill={e.color}>{e.label}</tspan>
                                    <tspan fill="#8A8A8A"> {e.dateText} </tspan>
                                    <tspan fill={e.color}>▲</tspan>
                                </>
                            ) : (
                                <>
                                    <tspan fill={e.color}>▲ {e.label}</tspan>
                                    <tspan fill="#8A8A8A"> {e.dateText}</tspan>
                                </>
                            )}
                        </text>
                    ))}
                </g>
            )}

            {/* 범례 */}
            {L.legend.map((r, i) => (
                <g key={`r${i}`}>
                    {i > 0 && <line x1={28} x2={right} y1={r.y - 13} y2={r.y - 13} stroke="#EFEFEF" />}
                    <LegendChip row={r} x={28} y={r.y} P={P} />
                    <text x={68} y={r.y + 4.5} fontSize="13" fontWeight="500" fill={r.muted ? '#9A9A9A' : '#222'}>{r.name}</text>
                    {(r.combo || r.badge) && (
                        <g>
                            <rect x={68 + r.nameWidth + 8} y={r.y - 8} width={36} height={16} rx={8} fill="#EFEEEA" />
                            <text x={68 + r.nameWidth + 26} y={r.y + 3.5} fontSize="10.5" fill="#6B6B6B" textAnchor="middle">
                                {r.badge || t('병용')}
                            </text>
                        </g>
                    )}
                    <text x={528} y={r.y + 4.5} fontSize="12.5" fill={r.muted ? '#9A9A9A' : '#444'}>{r.dates}</text>
                    <text x={right} y={r.y + 4.5} fontSize="12.5" fontWeight="600" fill="#1F2328" textAnchor="end">{r.months}</text>
                </g>
            ))}
        </svg>
    );
});

export default TreatmentTimelineChart;
